import { useEffect, useRef, useState } from "react"
import { Box, CircularProgress, InputBase, Tooltip, Typography } from "@mui/material"

/**
 * Text you can change where it is written.
 *
 * ── Why not a dialog ────────────────────────────────────────────────────────
 *
 * Fixing a typo in a task title should not be: open the record, find the form,
 * change one character, save, close. That is five actions for one, and the cost
 * is that the typo stays there.
 *
 * ── Why it saves on blur as well as Enter ───────────────────────────────────
 *
 * Because people click away, and a field that silently discards what they typed
 * because they did not press the right key is worse than no inline editing at
 * all. Escape is the way out that discards, and it says so in the tooltip.
 *
 * ── Why it shows the old value while saving ─────────────────────────────────
 *
 * Optimistic, but honest: the new text is on screen immediately with a small
 * spinner beside it, and if the save fails the text snaps back and the error is
 * raised. Showing the new value and never checking is how somebody walks away
 * believing they renamed something.
 */
export default function InlineEdit({
  value,
  onSave,
  variant = "body2",
  /**
   * The element the text is rendered as when it is not being edited.
   *
   * It matters: a task title is an `h3`, and making it editable must not
   * quietly demote it to a span. Headings are how somebody using a screen
   * reader skims a panel, and losing one is losing the only landmark on it.
   */
  component,
  placeholder = "Untitled",
  disabled,
  sx,
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value ?? "")
  const [busy, setBusy] = useState(false)
  const inputRef = useRef(null)

  useEffect(() => {
    if (!editing) setDraft(value ?? "")
  }, [value, editing])

  const commit = async () => {
    const next = draft.trim()
    setEditing(false)
    if (!next || next === (value ?? "").trim()) {
      setDraft(value ?? "")
      return
    }
    setBusy(true)
    try {
      await onSave(next)
    } catch {
      // The caller owns the message; this only puts the text back.
      setDraft(value ?? "")
    } finally {
      setBusy(false)
    }
  }

  if (disabled) {
    return (
      <Typography variant={variant} component={component} sx={sx}>
        {value || placeholder}
      </Typography>
    )
  }

  if (editing) {
    return (
      <InputBase
        inputRef={inputRef}
        autoFocus
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault()
            commit()
          } else if (event.key === "Escape") {
            event.preventDefault()
            setDraft(value ?? "")
            setEditing(false)
          }
        }}
        fullWidth
        sx={{
          ...sx,
          px: 0.75,
          py: 0.25,
          borderRadius: 1,
          border: 1,
          borderColor: "primary.main",
          font: "inherit",
        }}
        inputProps={{ "aria-label": "Edit" }}
      />
    )
  }

  return (
    <Tooltip title="Click to rename · Esc to cancel" enterDelay={600}>
      <Box
        component="span"
        onClick={() => setEditing(true)}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          px: 0.75,
          py: 0.25,
          mx: -0.75,
          borderRadius: 1,
          cursor: "text",
          /* The affordance is the hover, not a pencil: an icon on every
             editable string is a screen full of pencils. */
          transition: "background-color 140ms ease",
          "&:hover": { bgcolor: "action.hover" },
          ...sx,
        }}
      >
        <Typography
          variant={variant}
          component={component || "span"}
          color={value ? "text.primary" : "text.secondary"}
          sx={{ m: 0 }}
        >
          {value || placeholder}
        </Typography>
        {busy ? <CircularProgress size={12} /> : null}
      </Box>
    </Tooltip>
  )
}
